import React, { useState, useEffect } from 'react';
import { 
  Dropdown, 
  Button, 
  Space, 
  Typography, 
  Spin
} from 'antd';
import { 
  DatabaseOutlined,
  ApartmentOutlined,
  DownOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useChatUI } from '../../contexts/ChatUIContext';
import { useUser } from '../../contexts/UserContext';
import { ModelType, ModelSelection } from '../../types/models';
import { ThreadModelService } from '../../services/api/threadModelService';
import { ArtifactService } from '../../services/api/artifactService';
import type { PrimeArtifact } from '../../types/primeModel';
import type { ArtifactTree } from '../../types/artifacts';

const { Text } = Typography;

export const ChatUIModelSelector: React.FC = () => {
  const { model, setModel } = useChatUI();
  const { selectedCompany, availableCompanies } = useUser();
  const [primeArtifacts, setPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [companyTrees, setCompanyTrees] = useState<ArtifactTree[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Load models when component mounts or company changes
  useEffect(() => {
    void loadModels();
  }, [selectedCompany?.id]);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load prime artifacts
      const primeRoots = await ThreadModelService.getPrimeRoots();
      setPrimeArtifacts(primeRoots);

      // Load company trees
      if (selectedCompany?.id) {
        const accessibleCompanyIds = availableCompanies.map(uc => uc.company.id);
        const trees = await ArtifactService.getTrees(selectedCompany.id, accessibleCompanyIds);
        setCompanyTrees(trees);
      } else {
        setCompanyTrees([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load models');
      console.error('Failed to load models:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleModelTypeChange = (newType: ModelType) => {
    if (newType === ModelType.PRIME) {
      // Set to first prime artifact if available
      if (primeArtifacts.length > 0) {
        setModel({
          type: ModelType.PRIME,
          artifactId: primeArtifacts[0].id,
        });
      } else {
        setModel({ type: ModelType.PRIME });
      }
    } else if (newType === ModelType.COMPANY) {
      // Set to first company tree if available
      if (companyTrees.length > 0) {
        setModel({
          type: ModelType.COMPANY,
          artifactTreeId: companyTrees[0].id,
        });
      } else {
        setModel({ type: ModelType.COMPANY });
      }
    }
    // Close dropdown after selection
    setDropdownOpen(false);
  };

  const handlePrimeArtifactChange = (artifactId: string) => {
    setModel({
      type: ModelType.PRIME,
      artifactId,
    });
    // Close dropdown after selection
    setDropdownOpen(false);
  };

  const handleCompanyTreeChange = (artifactTreeId: string) => {
    setModel({
      type: ModelType.COMPANY,
      artifactTreeId,
    });
    // Close dropdown after selection
    setDropdownOpen(false);
  };

  const getModelTypeIcon = (type: ModelType) => {
    switch (type) {
      case ModelType.PRIME:
        return <ApartmentOutlined style={{ color: '#1890ff' }} />;
      case ModelType.COMPANY:
        return <DatabaseOutlined style={{ color: '#52c41a' }} />;
      default:
        return <DatabaseOutlined />;
    }
  };

  const getCurrentModelName = () => {
    if (!model) return 'No Model';
    
    if (model.type === ModelType.PRIME && model.artifactId) {
      const artifact = primeArtifacts.find(a => a.id === model.artifactId);
      return artifact?.name || 'Prime Model';
    } else if (model.type === ModelType.COMPANY && model.artifactTreeId) {
      const tree = companyTrees.find(t => t.id === model.artifactTreeId);
      return tree?.name || 'Company Model';
    }
    
    return model.type === ModelType.PRIME ? 'Prime Model' : 'Company Model';
  };

  // Create model type menu items
  const modelTypeItems = [
    {
      key: ModelType.PRIME,
      label: (
        <Space>
          <ApartmentOutlined />
          <Text>Prime Model</Text>
          <Text type="secondary">(Template artifacts)</Text>
        </Space>
      ),
      onClick: () => handleModelTypeChange(ModelType.PRIME)
    },
    {
      key: ModelType.COMPANY,
      label: (
        <Space>
          <DatabaseOutlined />
          <Text>Company Model</Text>
          <Text type="secondary">(Business instances)</Text>
        </Space>
      ),
      onClick: () => handleModelTypeChange(ModelType.COMPANY)
    }
  ];

  // Create prime artifact menu items
  const getPrimeArtifactItems = () => {
    return primeArtifacts.map(artifact => ({
      key: artifact.id,
      label: (
        <div style={{ padding: '4px 0' }}>
          <div>
            <Text strong>{artifact.name}</Text>
          </div>
        </div>
      ),
      onClick: () => handlePrimeArtifactChange(artifact.id)
    }));
  };

  // Create company tree menu items
  const getCompanyTreeItems = () => {
    return companyTrees.map(tree => ({
      key: tree.id,
      label: (
        <div style={{ padding: '4px 0' }}>
          <div>
            <Text strong>{tree.name}</Text>
          </div>
        </div>
      ),
      onClick: () => handleCompanyTreeChange(tree.id)
    }));
  };

  // Create the main menu
  const menuItems = [
    // Model Type Selection
    {
      key: 'modelType',
      label: (
        <div>
          <Text strong>Model Type:</Text>
          <div style={{ marginTop: '8px' }}>
            {modelTypeItems.map(item => (
              <div
                key={item.key}
                onClick={item.onClick}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  margin: '4px 0',
                  backgroundColor: model?.type === item.key ? '#e6f7ff' : 'transparent',
                  border: model?.type === item.key ? '1px solid #91d5ff' : '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (model?.type !== item.key) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (model?.type !== item.key) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        </div>
      ),
      type: 'group' as const
    },
    
    // Model Selection (only if model type is selected)
    ...(model ? [
      {
        type: 'divider' as const
      },
      {
        key: 'model',
        label: (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>Select Model:</Text>
              <Button 
                size="small" 
                icon={<ReloadOutlined />} 
                onClick={(e) => {
                  e.stopPropagation();
                  void loadModels();
                }}
                loading={loading}
              />
            </div>
            {loading ? (
              <div style={{ padding: '8px 0', textAlign: 'center' }}>
                <Spin size="small" />
              </div>
            ) : error ? (
              <div style={{ padding: '8px 0', color: '#ff4d4f' }}>
                <Text type="danger" style={{ fontSize: '12px' }}>{error}</Text>
              </div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '8px' }}>
                {model.type === ModelType.PRIME ? (
                  getPrimeArtifactItems().map(item => (
                    <div
                      key={item.key}
                      onClick={item.onClick}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        margin: '4px 0',
                        backgroundColor: model.artifactId === item.key ? '#e6f7ff' : 'transparent',
                        border: model.artifactId === item.key ? '1px solid #91d5ff' : '1px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (model.artifactId !== item.key) {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (model.artifactId !== item.key) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {item.label}
                    </div>
                  ))
                ) : (
                  getCompanyTreeItems().map(item => (
                    <div
                      key={item.key}
                      onClick={item.onClick}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        margin: '4px 0',
                        backgroundColor: model.artifactTreeId === item.key ? '#e6f7ff' : 'transparent',
                        border: model.artifactTreeId === item.key ? '1px solid #91d5ff' : '1px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (model.artifactTreeId !== item.key) {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (model.artifactTreeId !== item.key) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {item.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ),
        type: 'group' as const
      }
    ] : [])
  ];

  const menu = {
    items: menuItems,
    style: { minWidth: '280px' }
  };

  return (
    <Dropdown 
      menu={menu} 
      trigger={['click']}
      placement="bottomLeft"
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
    >
      <Button 
        type="text"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          width: '100%',
          height: '40px',
          borderRadius: '6px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: 'white'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        }}
      >
        <Space size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space size="small">
            {model ? getModelTypeIcon(model.type) : <DatabaseOutlined />}
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', lineHeight: '1.2', color: 'white' }}>
                {model ? (model.type === ModelType.PRIME ? 'Prime' : 'Company') : 'Select Model'}
              </div>
              {model && (
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.2' }}>
                  {getCurrentModelName()}
                </div>
              )}
            </div>
          </Space>
          <DownOutlined style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }} />
        </Space>
      </Button>
    </Dropdown>
  );
};

